import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SpecificationsForm = ({ data, onChange }) => {
  const [errors, setErrors] = useState({});

  const trailerTypes = [
    { value: 'utility', label: 'Utility Trailer' },
    { value: 'enclosed', label: 'Enclosed Trailer' },
    { value: 'flatbed', label: 'Flatbed Trailer' },
    { value: 'car-hauler', label: 'Car Hauler' },
    { value: 'dump', label: 'Dump Trailer' },
    { value: 'equipment', label: 'Equipment Trailer' },
    { value: 'boat', label: 'Boat Trailer' },
    { value: 'livestock', label: 'Livestock Trailer' },
    { value: 'specialty', label: 'Specialty Trailers' }
  ];

  const hitchSizes = [
    { value: '1-7/8"', label: '1-7/8 inch' },
    { value: '2"', label: '2 inch' },
    { value: '2-5/16"', label: '2-5/16 inch' },
    { value: 'gooseneck', label: 'Gooseneck' },
    { value: 'fifth-wheel', label: 'Fifth Wheel' }
  ];

  const availableFeatures = [
    'Electric Brakes',
    'Ramp Gate',
    'Spare Tire',
    'LED Lights',
    'Tie-Down Rings',
    'Toolbox',
    'Side Rails',
    'Removable Sides',
    'Weatherproof Cover',
    'Hydraulic Lift'
  ];

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'trailerType':
        if (!value) {
          error = 'Trailer type is required';
        }
        break;
      case 'length':
        if (!value) {
          error = 'Length is required';
        } else if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          error = 'Length must be a positive number';
        } else if (parseFloat(value) > 100) {
          error = 'Length seems unusually large (max 100 ft)';
        }
        break;
      case 'width':
        if (!value) {
          error = 'Width is required';
        } else if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          error = 'Width must be a positive number';
        } else if (parseFloat(value) > 20) {
          error = 'Width seems unusually large (max 20 ft)';
        }
        break;
      case 'height':
        if (value && (isNaN(parseFloat(value)) || parseFloat(value) <= 0)) {
          error = 'Height must be a positive number';
        } else if (value && parseFloat(value) > 20) {
          error = 'Height seems unusually large (max 20 ft)';
        }
        break;
      case 'weightCapacity':
        if (!value) {
          error = 'Weight capacity is required';
        } else if (isNaN(parseFloat(value?.replace(/,/g, ''))) || parseFloat(value?.replace(/,/g, '')) <= 0) {
          error = 'Weight capacity must be a positive number';
        } else if (parseFloat(value?.replace(/,/g, '')) > 100000) {
          error = 'Weight capacity seems unusually large (max 100,000 lbs)';
        }
        break;
      case 'hitchSize':
        if (!value) {
          error = 'Hitch size is required';
        }
        break;
      case 'year':
        if (value && (isNaN(parseInt(value)) || parseInt(value) < 1900 || parseInt(value) > new Date()?.getFullYear() + 1)) {
          error = `Year must be between 1900 and ${new Date()?.getFullYear() + 1}`;
        }
        break;
      case 'vin':
        if (value && value?.length !== 17) {
          error = 'VIN must be exactly 17 characters';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (field, value) => {
    onChange({ ...data, [field]: value });
    
    // Clear error for this field when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Real-time validation for immediate feedback
    let error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const toggleFeature = (feature) => {
    const currentFeatures = data?.features || [];
    const newFeatures = currentFeatures?.includes(feature)
      ? currentFeatures?.filter(f => f !== feature)
      : [...currentFeatures, feature];
    handleInputChange('features', newFeatures);
  };

  // Validate all required fields on mount and when data changes
  useEffect(() => {
    const requiredFields = ['trailerType', 'length', 'width', 'weightCapacity', 'hitchSize'];
    const newErrors = {};
    
    requiredFields?.forEach(field => {
      let error = validateField(field, data?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
          Trailer Specifications
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Provide detailed information about your trailer
        </p>
      </div>

      <Select
        label="Trailer Type"
        placeholder="Select trailer type"
        options={trailerTypes}
        value={data?.trailerType}
        onChange={(value) => handleInputChange('trailerType', value)}
        required
        id="trailerType"
        name="trailerType"
        description=""
        error={errors?.trailerType}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Year"
          type="number"
          placeholder="2020"
          value={data?.year}
          onChange={(e) => handleInputChange('year', e?.target?.value)}
          description="Manufacturing year"
          error={errors?.year}
        />
        <Input
          label="Make"
          type="text"
          placeholder="Big Tex"
          value={data?.make}
          onChange={(e) => handleInputChange('make', e?.target?.value)}
          description="Manufacturer name"
        />
        <Input
          label="Model"
          type="text"
          placeholder="70PI-16"
          value={data?.model}
          onChange={(e) => handleInputChange('model', e?.target?.value)}
          description="Model number"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Length"
          type="text"
          placeholder="e.g., 16 ft"
          value={data?.length}
          onChange={(e) => handleInputChange('length', e?.target?.value)}
          required
          description="Total length of trailer"
          error={errors?.length}
        />
        <Input
          label="Width"
          type="text"
          placeholder="e.g., 6.5 ft"
          value={data?.width}
          onChange={(e) => handleInputChange('width', e?.target?.value)}
          required
          description="Width of trailer bed"
          error={errors?.width}
        />
        <Input
          label="Height"
          type="text"
          placeholder="e.g., 5 ft"
          value={data?.height}
          onChange={(e) => handleInputChange('height', e?.target?.value)}
          description="Height (if enclosed)"
          error={errors?.height}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Weight Capacity"
          type="text"
          placeholder="e.g., 3,500 lbs"
          value={data?.weightCapacity}
          onChange={(e) => handleInputChange('weightCapacity', e?.target?.value)}
          required
          description="Maximum load capacity"
          error={errors?.weightCapacity}
        />
        <Select
          label="Hitch Size"
          placeholder="Select hitch size"
          options={hitchSizes}
          value={data?.hitchSize}
          onChange={(value) => handleInputChange('hitchSize', value)}
          required
          id="hitchSize"
          name="hitchSize"
          description=""
          error={errors?.hitchSize}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="VIN (Optional)"
          type="text"
          placeholder="1HGBH41JXMN109186"
          value={data?.vin}
          onChange={(e) => handleInputChange('vin', e?.target?.value)}
          description="Vehicle Identification Number"
          error={errors?.vin}
        />
        <Input
          label="License Plate (Optional)"
          type="text"
          placeholder="ABC-1234"
          value={data?.licensePlate}
          onChange={(e) => handleInputChange('licensePlate', e?.target?.value)}
          description="Current license plate number"
        />
      </div>

      <div>
        <label className="block font-caption text-sm font-medium text-foreground mb-3">
          Special Features
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {availableFeatures?.map((feature) => (
            <button
              key={feature}
              type="button"
              onClick={() => toggleFeature(feature)}
              className={`p-3 rounded-md border-2 transition-smooth text-left ${
                data?.features?.includes(feature)
                  ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center space-x-2">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-smooth ${
                    data?.features?.includes(feature)
                      ? 'border-primary bg-primary' :'border-border'
                  }`}
                >
                  {data?.features?.includes(feature) && (
                    <Icon name="Check" size={14} color="white" strokeWidth={3} />
                  )}
                </div>
                <span className="font-body text-sm text-foreground">{feature}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 bg-muted rounded-md flex items-start space-x-3">
        <Icon name="Info" size={20} color="var(--color-primary)" strokeWidth={2} />
        <div className="flex-1">
          <p className="font-body text-sm text-foreground">
            <span className="font-semibold">Tip:</span> Accurate specifications help renters find the right trailer for their needs and reduce booking cancellations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SpecificationsForm;